import { Component, Output, ViewEncapsulation, Input, EventEmitter, OnInit, ElementRef } from '@angular/core';
import { EVENTS } from 'src/app/_helpers/constants';
import { EventsService } from 'src/app/_shared/providers';
import { RMSOutcome as RMSOutcomeErroMap } from 'sharedweb/providers/utProto/enums_pb';
import { OrderNotificationParams } from 'src/app/app.interfaces';
import { orderMessageTemplates, OrderStatusColors, OrderStatusDisplayText, OrderStatusGroups, OrderStatusGroupType } from 'src/app/_shared/providers/order-notification/order-notifications.structs';
import { Utils } from 'src/app/_helpers';
import { OrderStatusGroup, Themes } from 'src/app/app.structs';
import { commonColors } from 'src/app/_helpers/colors.utilities';
import { UserBooksService } from 'src/app/dashboard/_modules/trading/_providers/user-books/user-books.service';
import { Holding } from 'sharedweb/app.models';
import { SharedUtils } from 'sharedweb/helpers/shared.utilities';
import { WEB_SM_FeEventType, WEB_SM_OrderStatus } from 'sharedweb/providers/utProto/webSmartOrder_pb';

@Component({
  selector: 'app-order-form-nudge',
  templateUrl: './order-form-nudge.component.html',
  styleUrls: ['./order-form-nudge.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class OrderFormNudgeComponent implements OnInit {
  showAddFundsButton: boolean = false;
  showAuthorizeButton: boolean = false;
  statusColor: string;
  displayStatus: string;
  @Input() data: OrderNotificationParams;
  @Output() closed = new EventEmitter<void>();
  orderText: string;
  statusText: string;

  constructor(
    private _eventsService: EventsService,
    private elementRef: ElementRef,
    private _userBooksService: UserBooksService
  ) { }

  ngOnInit() {
    const isGTT = this.data?.is_gtt;
    const isSmart = this.data?.is_smart_order || this.data?.smart_order_status;
    if (isGTT) {
      // Handle GTT-specific status mapping using event_type
      const gttStatusGroup = this.getGTTStatusGroup(this.data.gtt_event_type);
      this.displayStatus = this.getDisplayStatus(gttStatusGroup);
      this.statusColor = this.getStatusColor(gttStatusGroup);
      
      const template = orderMessageTemplates[gttStatusGroup](this.data);
      this.orderText = template.orderText;
      this.statusText = template.statusText;
    } else if(this.data?.is_super) {
      const superStatusGroup = this.getSuperStatusGroup(this.data.super_event_type);
      this.displayStatus = this.getDisplayStatus(superStatusGroup);
      this.statusColor = this.getStatusColor(superStatusGroup);
      const template = orderMessageTemplates[superStatusGroup](this.data);
      this.orderText = template.orderText;
      this.statusText = template.statusText;
    } else if(isSmart) {
      const smartStatusGroup = this.getSmartStatusGroup(this.data.smart_order_status);
      this.displayStatus = this.getDisplayStatus(smartStatusGroup);
      this.statusColor = this.getStatusColor(smartStatusGroup);

      const template = orderMessageTemplates[smartStatusGroup](this.data);
      this.orderText = template.orderText;
      this.statusText = template.statusText;
    } else {
    const statusGroup = this.getStatusGroup(this.data.order_status.toLowerCase());
    // Check if this is a replaced order

    const isReplaced = this.data.original_client_order_id !== '0' &&
      (statusGroup === 'Confirmed' || statusGroup === 'Partially_Filled');

    // If it's a replaced order, use "Replaced" as the status for display purposes
    this.displayStatus = isReplaced ? OrderStatusDisplayText.Replaced : this.getDisplayStatus(statusGroup);

    this.statusColor = isReplaced ? OrderStatusColors.Replaced : this.getStatusColor(statusGroup);

    this.showAddFundsButton = this.data.error_code === RMSOutcomeErroMap.RMSOUTCOME_MARGIN_CHECK_FAILED;
    this.showAuthorizeButton = this.data.error_code === RMSOutcomeErroMap.RMSOUTCOME_INSUFFICIENT_AUTHORIZED_HOLDINGS;

    // If the order is placed or replaced, show the confirmed message, else show the status group message.
    const template = (statusGroup === OrderStatusGroup.Placed || isReplaced)
      ? orderMessageTemplates.Confirmed(this.data)
      : orderMessageTemplates[statusGroup](this.data);
    this.orderText = template.orderText;
    this.statusText = template.statusText;
  }
    this.applyStyles();
  }

  private applyStyles() {
    const isDark = Utils.storageHelper.themeSelected === Themes.Dark;
    const element = this.elementRef.nativeElement;

    // Apply colors to the main container
    const bgColor = isDark ? commonColors.orderToastDarkBackground : commonColors.orderToastLightBackground;
    const textColor = isDark ? commonColors.orderToastDarkText : commonColors.orderToastLightText;

    // Apply to the main container
    element.style.setProperty('background-color', bgColor);
    element.style.setProperty('color', textColor);

    // Apply to all inner containers to ensure full coverage
    const containers = element.querySelectorAll('.order-form-nudge, .notification-content, .notification-body');
    containers.forEach(container => {
      container.style.backgroundColor = bgColor;
      container.style.color = textColor;
    });

  }

  navigateToAuthorize(): void {
    // Get the specific holding for the rejected symbol using isin from the order data
    // Note: isin might not be in OrderNotificationParams interface but is present in rejection data
    const isin = (this.data as any)?.isin;
    if (isin) {
      const holdingKey = `${SharedUtils.storageHelper.userInfo.username}:${isin}`;
      const cachedHoldings = this._userBooksService.cachedHoldings;
      let selectedHoldingResponse = [];
      if (cachedHoldings?.data?.[holdingKey]) {
        selectedHoldingResponse = [new Holding(cachedHoldings.data[holdingKey])];
      }
      if (selectedHoldingResponse?.length) {
        // Publish event with only the specific holding for the rejected symbol
        this._eventsService.publish(EVENTS.OPEN_AUTHORIZATION_DIALOG, selectedHoldingResponse);
      }
    }
    this.close();
  }
  
  // getting the status color based on the status set
  getStatusColor(status: string): string {
     // Check if it's a GTT status group first
    if (status.startsWith('GTT_')) {
      return OrderStatusColors[status] || OrderStatusColors.Confirmed;
    }
    if(status.startsWith('SUPER_')) {
      return OrderStatusColors[status] || OrderStatusColors.Confirmed;
    }
    if (status.startsWith('Smart_')) {
      return OrderStatusColors[status] || OrderStatusColors.Confirmed;
    }
    const group = this.getStatusGroup(status);
    return group ? OrderStatusColors[group] : OrderStatusColors.Confirmed;
  }

  // getting the display status based on the status set
  getDisplayStatus(status: string): string {
    // Check if it's a GTT status group first
    if (status.startsWith('GTT_')) {
      return OrderStatusDisplayText[status] || status?.charAt(0).toUpperCase() + status?.slice(1);
    }
    if(status.startsWith('SUPER_')) {
     return OrderStatusDisplayText[status] || status?.charAt(0).toUpperCase() + status?.slice(1);
    }
    if (status.startsWith('Smart_')) {
      return OrderStatusDisplayText[status] || status?.charAt(0).toUpperCase() + status?.slice(1);
    }
    const group = this.getStatusGroup(status);
    return group ? OrderStatusDisplayText[group] : status?.charAt(0).toUpperCase() + status?.slice(1);
  }

  // getting the status group based on the status set
  getStatusGroup(status: string): OrderStatusGroupType {
    const lowerStatus = status?.toLowerCase();
    return (Object.entries(OrderStatusGroups)
      .find(([_, statuses]) => statuses.includes(lowerStatus))?.[0] as OrderStatusGroupType);
  }
  
  getGTTStatusGroup(eventType: string): string {
    switch (eventType) {
      case 'order_created':
        return 'GTT_Confirmed';
      case 'order_modified':
        return 'GTT_Replaced';
      case 'order_cancelled':
        return 'GTT_Cancelled';
      case 'order_triggered':
        return 'GTT_Triggered';
      default:
        return 'GTT_Confirmed';
    }
  }

  getSuperStatusGroup(eventType: number): string {
    switch (eventType) {
      case WEB_SM_FeEventType.FE_EVENT_TYPE_CREATED:
        return 'SUPER_CREATED';
      case WEB_SM_FeEventType.FE_EVENT_TYPE_ACTIVE:
        return 'SUPER_ACTIVE';
      case WEB_SM_FeEventType.FE_EVENT_TYPE_MODIFIED:
        return 'SUPER_MODIFIED';
      case WEB_SM_FeEventType.FE_EVENT_TYPE_FAILED:
        return 'SUPER_FAILED';
      case WEB_SM_FeEventType.FE_EVENT_TYPE_CANCELLED:
        return 'SUPER_CANCELLED';
      case WEB_SM_FeEventType.FE_EVENT_TYPE_COMPLETED:
        return 'SUPER_COMPLETED';
      default:
        return 'SUPER_CREATED';
    }
  }
  getSmartStatusGroup(smartStatus: number): string {
    switch (smartStatus) {
      case WEB_SM_OrderStatus.ORDER_STATUS_PENDING:
        return 'Smart_Pending';
      case WEB_SM_OrderStatus.ORDER_STATUS_ACTIVE:
        return 'Smart_Active';
      case WEB_SM_OrderStatus.ORDER_STATUS_CANCELLED:
        return 'Smart_Cancelled';
      case WEB_SM_OrderStatus.ORDER_STATUS_FAILED:
        return 'Smart_Failed';
      case WEB_SM_OrderStatus.ORDER_STATUS_COMPLETED:
        return 'Smart_Completed';
      case WEB_SM_OrderStatus.ORDER_STATUS_PAUSED:
        return 'Smart_Paused';
      default:
        return 'Smart_Pending';
    }
  }

  close() {
    this.closed.emit();
  }

  navigateToFunds(): void {
    this._eventsService.publish(EVENTS.DASH_TAB_CHANGED, 4);
    this.close();
  }
}
